import React, { useState, useEffect } from "react";
import { Row, Col, FormGroup, Button, FormFeedback } from "reactstrap";
import { useFormContext } from "react-hook-form";
import AnimatedErrorMessage from "../../../../../../toolkit/animatedErrorMessage";
import withStyles from "isomorphic-style-loader/withStyles";
import * as yup from "yup";
import s from "./styles.scss";
import WizardFormStep from "../../components/WizardFormStep";
import WizardFooter from "../../../../../../toolkit/wizardFooter";
import FormInput from "../../../../../../toolkit/formInput";
import ErrorAlert from "../../../../../../toolkit/errorAlert";
import {
  Delete as DeleteIcon,
  AddCircle as AddCircleIcon,
  Email as EmailIcon,
  Info as InfoIcon,
  AccountCircle as AccountCircleIcon, InfoOutline,
} from "@mui/icons-material";
import { IconButton, Chip } from "@mui/material";
import { createEmailField, createRequiredTextField } from "../../../../../../utils/validation/validationUtils";
import ICharityEmailSetup from "../../../../../../types/ICharityEmailSetup";
import { registerSendingEmails, SendingEmail } from "../../../../../../utils/fetch";
import { saveEmailSetupData } from "../../../../../../utils/firebase/emailSetup";
import IGlobalConfig from "../../../../../../types/IGlobalConfig";
import { useLoading } from "../../../../../../hooks/useLoading";
import { AlertColor } from "@mui/material";

const extractDomainFromEmail = (email: string): string | null => {
  if (!email || !email.includes('@')) return null;
  return email.split('@')[1]?.toLowerCase() || null;
};

const inferDomainFromEmails = (
  emails: Array<{ email?: string }>
): string => {
  const counts = new Map<string, number>();
  emails.forEach(item => {
    const domain = extractDomainFromEmail(item.email || "");
    if (!domain) {
      return;
    }
    counts.set(domain, (counts.get(domain) || 0) + 1);
  });

  let best = "";
  let bestCount = 0;
  counts.forEach((count, domain) => {
    if (count > bestCount) {
      best = domain;
      bestCount = count;
    }
  });
  return best;
};

const resolveSenderEmail = (raw: string, domain: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return trimmed;
  }
  if (!trimmed.includes("@") && domain) {
    return `${trimmed}@${domain}`;
  }
  return trimmed;
};

const validateEmailDomain = (email: string, validatedDomain: string): boolean => {
  if (!validatedDomain) return true;
  const emailDomain = extractDomainFromEmail(email);
  if (!emailDomain) return false;
  return emailDomain === validatedDomain.toLowerCase();
};

export const addDomainSchema = yup.object({
  tempSenderName: createRequiredTextField("Name"),
  tempSenderEmail: createEmailField(),
});

export const sendingDomainsSubmissionSchema = yup.object({
  sendingDomains: yup.array().of(
    yup.object({
      name: yup.string().required("Sender name is required"),
      email: yup.string()
        .email("Invalid email address")
        .required("Sender email is required"),
      defaultEmail: yup.boolean().required()
    })
  ).min(1, "At least one sending domain is required"),
});

interface SendingDomain {
  id: string;
  name: string;
  email: string;
  defaultEmail: boolean;
}

interface SendingDomainsStepProps {
  charityId?: string;
  emailSetupData?: ICharityEmailSetup | null;
  existingSendingEmails?: SendingEmail[];
  onExit?: () => void;
  config?: IGlobalConfig;
  serviceAccessToken?: string;
  showSnackbar?: (message: string, severity?: AlertColor) => void;
  finishButtonText?: string;
  hideBackButton?: boolean;
  footerAfterAddForm?: boolean;
  successMessage?: string;
}

const SendingDomainsStep: React.FC<SendingDomainsStepProps> = ({
  charityId,
  emailSetupData,
  existingSendingEmails = [],
  onExit,
  config,
  serviceAccessToken,
  showSnackbar,
  finishButtonText = "Complete Setup",
  hideBackButton = false,
  footerAfterAddForm = false,
  successMessage = "Email setup completed successfully!",
}) => {
  const { setValue, watch, trigger, setError, clearErrors, formState: { errors } } = useFormContext();
  const [domains, setDomains] = useState<SendingDomain[]>([]);
  const [hoveredDomainId, setHoveredDomainId] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const { loading, runWithLoading } = useLoading();

  useEffect(() => {
    if (existingSendingEmails.length > 0 && domains.length === 0) {
      const prepopulatedDomains: SendingDomain[] = existingSendingEmails.map((email, index) => ({
        id: `existing-${index}-${Date.now()}`,
        name: email.name,
        email: email.email,
        defaultEmail: email.defaultEmail,
      }));
      setDomains(prepopulatedDomains);
      setValue("sendingDomains", prepopulatedDomains, { shouldValidate: false });
    }
  }, [existingSendingEmails]);

  const tempSenderName = watch("tempSenderName") || "";
  const tempSenderEmail = watch("tempSenderEmail") || "";

  const domainResponseFromForm = watch("domainResponse");
  const currentDomain = (
    domainResponseFromForm?.domain ||
    emailSetupData?.sendingDomain?.domain ||
    inferDomainFromEmails(domains.length > 0 ? domains : existingSendingEmails) ||
    ""
  ).toLowerCase();

  useEffect(() => {
    if (errors.tempSenderEmail?.type === 'manual') {
      clearErrors('tempSenderEmail');
    }
  }, [tempSenderName, tempSenderEmail]);

  const handleAdd = async () => {
    const isNameValid = await trigger(["tempSenderName"]);
    if (!isNameValid) {
      return;
    }

    const resolvedEmail = resolveSenderEmail(tempSenderEmail, currentDomain);
    if (!resolvedEmail) {
      setError("tempSenderEmail", {
        type: "manual",
        message: "Sender email is required",
      });
      return;
    }
    if (!resolvedEmail.includes("@")) {
      setError("tempSenderEmail", {
        type: "manual",
        message: currentDomain
          ? `Enter a name to complete as name@${currentDomain}`
          : "Enter a full email address",
      });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resolvedEmail)) {
      setError("tempSenderEmail", {
        type: "manual",
        message: "Invalid email address",
      });
      return;
    }

    if (currentDomain && !validateEmailDomain(resolvedEmail, currentDomain)) {
      setError("tempSenderEmail", {
        type: "manual",
        message: `Email must use your current domain: ${currentDomain}`,
      });
      return;
    }

    const isDuplicate = domains.some(
      domain =>
        domain.name.toLowerCase() === tempSenderName.toLowerCase() &&
        domain.email.toLowerCase() === resolvedEmail.toLowerCase()
    );

    if (isDuplicate) {
      setError("tempSenderEmail", {
        type: "manual",
        message: "This sender name and email combination has already been added",
      });
      return;
    }

    const newDomain: SendingDomain = {
      id: Date.now().toString(),
      name: tempSenderName,
      email: resolvedEmail,
      defaultEmail: domains.length === 0,
    };
    const updatedDomains = [...domains, newDomain];
    setDomains(updatedDomains);
    setValue("sendingDomains", updatedDomains, { shouldValidate: true });

    setValue("tempSenderName", "");
    setValue("tempSenderEmail", "");
    clearErrors('tempSenderEmail');
  };

  const handleSetDefault = (id: string) => {
    const updatedDomains = domains.map(d => ({
      ...d,
      defaultEmail: d.id === id
    }));
    setDomains(updatedDomains);
    setValue("sendingDomains", updatedDomains, { shouldValidate: false });
  };

  const handleDelete = (id: string) => {
    const domainToDelete = domains.find(d => d.id === id);
    let updatedDomains = domains.filter(d => d.id !== id);

    if (domainToDelete?.defaultEmail && updatedDomains.length > 0) {
      updatedDomains[0].defaultEmail = true;
    }

    setDomains(updatedDomains);
    setValue("sendingDomains", updatedDomains, { shouldValidate: false });
  };

  const handleNext = async (data: any) => {
    setApiError(null);

    await runWithLoading(async () => {
      try {
        if (!config?.adminServiceUrl || !serviceAccessToken || !charityId) {
          throw new Error("Missing required configuration. Please try again.");
        }

        await registerSendingEmails(
          config.adminServiceUrl,
          charityId,
          serviceAccessToken,
          data.sendingDomains
        );
        await saveEmailSetupData(charityId, {
          sendingEmailsConfigured: true,
        });

        if (showSnackbar) {
          showSnackbar(successMessage, "success");
        }

        setTimeout(() => {
          if (onExit) {
            onExit();
          }
        }, 3000);
      } catch (error) {
        console.error("Failed to register sending emails:", error);
        const errorMessage = error instanceof Error
          ? error.message
          : "Failed to save sending domains. Please try again.";
        setApiError(errorMessage);
        throw error;
      }
    }, 600);
  };

  const footer = (
    <WizardFooter
      schema={sendingDomainsSubmissionSchema}
      onFinish={handleNext}
      finishButtonText={loading ? "Saving..." : finishButtonText}
      disabled={loading}
      hideBackButton={hideBackButton}
    />
  );

  return (
    <WizardFormStep
      title="Add Sending Email Address(s)"
      subtitle="List email address(s) used to send emails to contacts."
      fillHeight={!footerAfterAddForm}
    >
      <Row className="justify-content-center mt-4">
        <Col md={8}>
          {/* Add Section */}
          <div className={s.addSection}>
            <span className={s.sectionTitle}>Add New Sending Email</span>
            {currentDomain && (
              <div className={s.domainHelperInfo}>
                <InfoOutline sx={{ fontSize: 16, color: '#94a3b8' }} />
                <span className={s.domainHelperText}>
                  Sender emails must use{" "}
                  <strong>{currentDomain}</strong>. A name without @ becomes{" "}
                  <strong>{`name@${currentDomain}`}</strong>.
                </span>
              </div>
            )}
            <Row className="justify-content-center" style={{ gap: '16px 0' }}>
              <Col md={5}>
                <FormInput
                  name="tempSenderName"
                  type="text"
                  placeholder="Name"
                />
              </Col>
              <Col md={5}>
                <FormInput
                  name="tempSenderEmail"
                  type="text"
                  placeholder={
                    currentDomain ? `name or name@${currentDomain}` : "Sender Email"
                  }
                />
              </Col>
              <Col md={2} className="d-flex align-items-center justify-content-center">
                <IconButton
                  onClick={handleAdd}
                  className={s.addIconButton}
                  disabled={!tempSenderName.trim() || !tempSenderEmail.trim() || !!errors.tempSenderName || !!errors.tempSenderEmail}
                  aria-label="Add sending email"
                  title="Add sending email"
                  sx={{
                    color: '#16a34a',
                    width: 48,
                    height: 48,
                    '&:hover': {
                      color: '#15803d',
                      transform: 'scale(1.05)',
                      backgroundColor: 'rgba(22, 163, 74, 0.08)',
                    },
                    '&:disabled': {
                      color: '#d6d6d6',
                    }
                  }}
                >
                  <AddCircleIcon sx={{ fontSize: 32 }} />
                </IconButton>
              </Col>
            </Row>

            {/* Error display for sendingDomains validation */}
            <AnimatedErrorMessage
              show={!!errors.sendingDomains}
              message={errors.sendingDomains?.message?.toString()}
              variant="standalone"
            />
          </div>
          {footerAfterAddForm ? footer : null}

          {domains.length > 0 && (
            <>
              <hr className={s.sectionDivider} />
              <div className={s.domainsList}>
                <span className={s.sectionTitle}>Current Sending Emails</span>
                {domains.map((domain) => (
                  <div
                    key={domain.id}
                    className={s.domainItem}
                    onMouseEnter={() => setHoveredDomainId(domain.id)}
                    onMouseLeave={() => setHoveredDomainId(null)}
                  >
                    <div className={s.domainIcon}>
                      <AccountCircleIcon />
                    </div>
                    <div className={s.domainInfo}>
                      <span className={s.domainName}>{domain.name}</span>
                      <span className={s.domainSeparator}>•</span>
                      <span className={s.domainEmail}>{domain.email}</span>
                      {domain.defaultEmail && (
                        <Chip
                          label="Default"
                          size="small"
                          variant="filled"
                          sx={{
                            backgroundColor: '#f1f5f9',
                            color: '#64748B',
                            fontSize: '13px',
                            fontWeight: 500,
                            height: '20px',
                            marginLeft: '8px',
                            '& .MuiChip-label': {
                              padding: '0 8px'
                            }
                          }}
                        />
                      )}
                    </div>
                    <div className={`${s.domainActions} ${hoveredDomainId === domain.id ? s.domainActionsVisible : ''}`}>
                      {!domain.defaultEmail && (
                        <Button
                          color="link"
                          size="sm"
                          className={s.setDefaultButton}
                          onClick={() => handleSetDefault(domain.id)}
                        >
                          Set as default
                        </Button>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(domain.id)}
                        className={s.deleteButton}
                      >
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Col>
      </Row>

      {/* API Error Display */}
      {apiError && (
        <Row className="mt-3 justify-content-center">
          <Col md="8">
            <ErrorAlert
              message={apiError}
            />
          </Col>
        </Row>
      )}

      {footerAfterAddForm ? null : footer}
    </WizardFormStep>
  );
};

export default withStyles(s)(SendingDomainsStep);
